import {
	Calendar,
	DollarSign,
	Edit,
	ExternalLink,
	Globe,
	TrendingUp,
	X,
} from "lucide-react";
import type React from "react";
import { useState } from "react";
import type { MarketingGrant } from "../types";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Progress } from "./ui/progress";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";

interface MarketingGrantSectionProps {
	data?: MarketingGrant;
	readOnly?: boolean;
	onUpdate?: (data: MarketingGrant) => void;
}

function formatCurrency(amount: number): string {
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	}).format(amount);
}

function getGrantStatusVariant(
	status?: string,
): "success" | "warning" | "destructive" | "info" | "neutral" {
	switch (status) {
		case "Active":
			return "success";
		case "Pending":
			return "warning";
		case "Expired":
			return "destructive";
		case "Completed":
			return "info";
		default:
			return "neutral";
	}
}

function isExpiringSoon(dateStr?: string): boolean {
	if (!dateStr) return false;
	const expiry = new Date(dateStr);
	const now = new Date();
	const diffDays = (expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
	return diffDays <= 30 && diffDays > 0;
}

function isExpired(dateStr?: string): boolean {
	if (!dateStr) return false;
	return new Date(dateStr) < new Date();
}

const GRANT_STATUSES = ["Active", "Pending", "Expired", "Completed"] as const;

const MarketingGrantSection: React.FC<MarketingGrantSectionProps> = ({
	data,
	readOnly = false,
	onUpdate,
}) => {
	const [isEditing, setIsEditing] = useState(false);
	const [editData, setEditData] = useState<MarketingGrant>({});
	const [geoInput, setGeoInput] = useState("");

	if (!data && readOnly) return null;

	const hasSomeData =
		data?.total_grant_usd != null ||
		data?.grant_status ||
		data?.grant_expiration_date ||
		(data?.geos && data.geos.length > 0);
	if (!hasSomeData && readOnly) return null;

	const spent =
		data?.total_grant_usd != null && data?.funds_left != null
			? data.total_grant_usd - data.funds_left
			: null;
	const usagePercent =
		data?.total_grant_usd && spent != null
			? Math.round((spent / data.total_grant_usd) * 100)
			: null;

	const startEditing = () => {
		setEditData({ ...data } as MarketingGrant);
		setGeoInput("");
		setIsEditing(true);
	};

	const handleSave = () => {
		onUpdate?.(editData);
		setIsEditing(false);
	};

	const handleCancel = () => {
		setIsEditing(false);
		setEditData({});
	};

	const addGeo = () => {
		const trimmed = geoInput.trim();
		if (!trimmed) return;
		const existing = editData.geos ?? [];
		if (!existing.includes(trimmed)) {
			setEditData((prev) => ({ ...prev, geos: [...existing, trimmed] }));
		}
		setGeoInput("");
	};

	const removeGeo = (geo: string) => {
		setEditData((prev) => ({
			...prev,
			geos: (prev.geos ?? []).filter((g) => g !== geo),
		}));
	};

	if (isEditing && !readOnly) {
		return (
			<Card className="mb-4 p-6">
				<div className="mb-5 flex items-center justify-between">
					<h3 className="flex items-center gap-2 font-semibold text-(--color-foreground) text-lg">
						<TrendingUp size={18} className="text-(--color-primary)" />
						Marketing Grant
					</h3>
					<div className="flex gap-2">
						<Button size="sm" variant="outline" onClick={handleCancel}>
							<X size={14} />
							Cancel
						</Button>
						<Button size="sm" onClick={handleSave}>
							Save
						</Button>
					</div>
				</div>

				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
					<div>
						<label className="mb-1 block font-medium text-(--color-muted-foreground) text-xs uppercase tracking-wider">
							Total Grant (USD)
						</label>
						<Input
							type="number"
							min={0}
							value={editData.total_grant_usd ?? ""}
							onChange={(e) =>
								setEditData((prev) => ({
									...prev,
									total_grant_usd: e.target.value
										? Number(e.target.value)
										: undefined,
								}))
							}
							placeholder="e.g. 250000"
						/>
					</div>

					<div>
						<label className="mb-1 block font-medium text-(--color-muted-foreground) text-xs uppercase tracking-wider">
							Funds Remaining (USD)
						</label>
						<Input
							type="number"
							min={0}
							value={editData.funds_left ?? ""}
							onChange={(e) =>
								setEditData((prev) => ({
									...prev,
									funds_left: e.target.value
										? Number(e.target.value)
										: undefined,
								}))
							}
							placeholder="e.g. 100000"
						/>
					</div>

					<div>
						<label className="mb-1 block font-medium text-(--color-muted-foreground) text-xs uppercase tracking-wider">
							Grant Status
						</label>
						<Select
							value={editData.grant_status ?? ""}
							onValueChange={(v) =>
								setEditData((prev) => ({
									...prev,
									grant_status: v as MarketingGrant["grant_status"],
								}))
							}
						>
							<SelectTrigger>
								<SelectValue placeholder="Select status" />
							</SelectTrigger>
							<SelectContent>
								{GRANT_STATUSES.map((status) => (
									<SelectItem key={status} value={status}>
										{status}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					<div>
						<label className="mb-1 block font-medium text-(--color-muted-foreground) text-xs uppercase tracking-wider">
							Expiration Date
						</label>
						<Input
							type="date"
							value={editData.grant_expiration_date ?? ""}
							onChange={(e) =>
								setEditData((prev) => ({
									...prev,
									grant_expiration_date: e.target.value || undefined,
								}))
							}
						/>
					</div>

					<div>
						<label className="mb-1 block font-medium text-(--color-muted-foreground) text-xs uppercase tracking-wider">
							Denomination
						</label>
						<Input
							value={editData.denomination ?? ""}
							onChange={(e) =>
								setEditData((prev) => ({
									...prev,
									denomination: e.target.value || undefined,
								}))
							}
							placeholder="e.g. USD"
						/>
					</div>

					<div>
						<label className="mb-1 block font-medium text-(--color-muted-foreground) text-xs uppercase tracking-wider">
							Quarter / Year
						</label>
						<Input
							value={editData.quarter_year ?? ""}
							onChange={(e) =>
								setEditData((prev) => ({
									...prev,
									quarter_year: e.target.value || undefined,
								}))
							}
							placeholder="e.g. Q1 2025"
						/>
					</div>

					<div className="sm:col-span-2 lg:col-span-3">
						<label className="mb-1 block font-medium text-(--color-muted-foreground) text-xs uppercase tracking-wider">
							Agreement Link
						</label>
						<Input
							value={editData.agreement_link ?? ""}
							onChange={(e) =>
								setEditData((prev) => ({
									...prev,
									agreement_link: e.target.value || undefined,
								}))
							}
							placeholder="https://..."
						/>
					</div>

					<div className="sm:col-span-2 lg:col-span-3">
						<label className="mb-1 block font-medium text-(--color-muted-foreground) text-xs uppercase tracking-wider">
							Target Geographies
						</label>
						<div className="flex gap-2">
							<Input
								value={geoInput}
								onChange={(e) => setGeoInput(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === "Enter") {
										e.preventDefault();
										addGeo();
									}
								}}
								placeholder="Type a geography and press Enter"
								className="flex-1"
							/>
							<Button
								size="sm"
								variant="outline"
								onClick={addGeo}
								type="button"
							>
								Add
							</Button>
						</div>
						{editData.geos && editData.geos.length > 0 && (
							<div className="mt-2 flex flex-wrap gap-1.5">
								{editData.geos.map((geo) => (
									<Badge
										key={geo}
										variant="outline"
										className="flex items-center gap-1 text-xs"
									>
										{geo}
										<button
											type="button"
											onClick={() => removeGeo(geo)}
											className="ml-0.5 hover:text-(--color-error-500)"
										>
											<X size={10} />
										</button>
									</Badge>
								))}
							</div>
						)}
					</div>
				</div>
			</Card>
		);
	}

	return (
		<Card className="mb-4 p-6">
			<div className="mb-5 flex items-center justify-between">
				<h3 className="flex items-center gap-2 font-semibold text-(--color-foreground) text-lg">
					<TrendingUp size={18} className="text-(--color-primary)" />
					Marketing Grant
				</h3>
				<div className="flex items-center gap-2">
					{data?.grant_status && (
						<Badge variant={getGrantStatusVariant(data.grant_status)}>
							{data.grant_status}
						</Badge>
					)}
					{!readOnly && onUpdate && (
						<Button size="sm" variant="ghost" onClick={startEditing}>
							<Edit size={14} />
						</Button>
					)}
				</div>
			</div>

			{/* Funding overview */}
			{data?.total_grant_usd != null && (
				<div className="mb-4 rounded-(--radius-sm) border border-(--color-border) p-4">
					<div className="mb-3 flex items-start justify-between">
						<div>
							<span className="mb-1 block font-medium text-(--color-muted-foreground) text-xs uppercase tracking-wider">
								Total Grant
							</span>
							<span className="flex items-center gap-1 font-bold text-(--color-foreground) text-2xl">
								<DollarSign size={20} className="text-(--color-success-600)" />
								{formatCurrency(data.total_grant_usd)}
							</span>
						</div>
						{data?.funds_left != null && (
							<div className="text-right">
								<span className="mb-1 block font-medium text-(--color-muted-foreground) text-xs uppercase tracking-wider">
									Remaining
								</span>
								<span className="font-bold text-(--color-foreground) text-xl">
									{formatCurrency(data.funds_left)}
								</span>
							</div>
						)}
					</div>
					{usagePercent != null && (
						<div>
							<div className="mb-1 flex justify-between text-(--color-muted-foreground) text-xs">
								<span>{formatCurrency(spent ?? 0)} spent</span>
								<span>{usagePercent}% used</span>
							</div>
							<Progress value={usagePercent} className="h-2" />
						</div>
					)}
				</div>
			)}

			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
				{data?.grant_expiration_date && (
					<div
						className={`rounded-(--radius-sm) p-3 ${
							isExpired(data.grant_expiration_date)
								? "bg-(--color-error-50)"
								: isExpiringSoon(data.grant_expiration_date)
									? "bg-(--color-warning-50)"
									: "bg-(--color-muted)"
						}`}
					>
						<span className="mb-1 block font-medium text-(--color-muted-foreground) text-xs uppercase tracking-wider">
							Expiration Date
						</span>
						<span
							className={`flex items-center gap-1.5 font-semibold text-sm ${
								isExpired(data.grant_expiration_date)
									? "text-(--color-error-700)"
									: isExpiringSoon(data.grant_expiration_date)
										? "text-(--color-warning-700)"
										: "text-(--color-foreground)"
							}`}
						>
							<Calendar size={14} />
							{new Date(data.grant_expiration_date).toLocaleDateString(
								"en-US",
								{
									month: "short",
									day: "numeric",
									year: "numeric",
								},
							)}
							{isExpired(data.grant_expiration_date) && " (Expired)"}
							{isExpiringSoon(data.grant_expiration_date) && " (Expiring soon)"}
						</span>
					</div>
				)}

				{data?.denomination && (
					<div className="rounded-(--radius-sm) bg-(--color-muted) p-3">
						<span className="mb-1 block font-medium text-(--color-muted-foreground) text-xs uppercase tracking-wider">
							Denomination
						</span>
						<span className="font-semibold text-(--color-foreground) text-sm">
							{data.denomination}
						</span>
					</div>
				)}

				{data?.quarter_year && (
					<div className="rounded-(--radius-sm) bg-(--color-muted) p-3">
						<span className="mb-1 block font-medium text-(--color-muted-foreground) text-xs uppercase tracking-wider">
							Quarter / Year
						</span>
						<span className="font-semibold text-(--color-foreground) text-sm">
							{data.quarter_year}
						</span>
					</div>
				)}
			</div>

			{/* Geos */}
			{data?.geos && data.geos.length > 0 && (
				<div className="mt-4 rounded-(--radius-sm) bg-(--color-muted) p-3">
					<span className="mb-2 flex items-center gap-1.5 font-medium text-(--color-muted-foreground) text-xs uppercase tracking-wider">
						<Globe size={12} />
						Target Geographies
					</span>
					<div className="flex flex-wrap gap-1.5">
						{data.geos.map((geo) => (
							<Badge key={geo} variant="outline" className="text-xs">
								{geo}
							</Badge>
						))}
					</div>
				</div>
			)}

			{/* Agreement link */}
			{data?.agreement_link && (
				<div className="mt-4">
					<a
						href={data.agreement_link}
						target="_blank"
						rel="noopener noreferrer"
						className="inline-flex items-center gap-1.5 font-medium text-(--color-primary) text-sm hover:underline"
					>
						<ExternalLink size={14} />
						View Grant Agreement
					</a>
				</div>
			)}
		</Card>
	);
};

export default MarketingGrantSection;
